import React from 'react';

import EllipsisTextHint from '@pb/kit-component-ellipsis-text-hint';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import {Button} from '../button';

import styles from './filter-pill.component.module.styl';

import type {ButtonProps} from '../button';
import type {ReactNode} from 'react';


const classNames = withCssModule(styles);
const classes = hashedClasses(styles);

export interface FilterPillProps extends Omit<ButtonProps, 'variant'> {
    /**
     * Pill count
     */
    count?: ReactNode,
    /**
     * Disable browser hint
     */
    disableBrowserHint?: boolean,
    /**
     * Selected pill state
     * @deprecated It will be removed in 6 months 10-02-2026. Use aria-pressed instead.
     */
    selected?: boolean,
}

/**
 * @description The `FilterPill` component extends the `Button` component to provide additional features
 * such as displaying a count and handling a selected state.
 * It is designed for use in filter-like UIs, where users can select or deselect options represented by buttons.
 *
 * ## Features
 * - Displays a label with an optional count beside it to indicate the number of selected items.
 * - Supports a `selected` state to visually indicate if the button is active
 * - Extends all functionalities of the `Button` component, including click, keyboard interactions, and custom styles
 *
 * ## Accessibility
 * - Uses `aria-pressed` to indicate the selected state for assistive technologies
 * - Uses `aria-disabled` to indicate the disabled state for assistive technologies
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/buttons-button-filter-pill--documentation}
 */

export const FilterPill = (props: FilterPillProps) => {
    const {
        children,
        className,
        count,
        disableBrowserHint,
        disabled,
        selected,
        ...buttonProps
    } = props;

    const filterButtonClassName = classNames(
        'filter-pill', 'filter-pill__icon',
        className,
        {
            'filter-pill_selected': props['aria-pressed'] || selected,
            'filter-pill_disabled': disabled,
        },
    );

    const pillName = (
        <span>
            {children}
            {count ? <span className={classes['filter-pill__count']}>({count})</span> : null}
        </span>
    );

    return (
        <Button
            disabled={disabled}
            aria-pressed={selected}
            {...buttonProps}
            variant="Type.SECONDARY"
            className={filterButtonClassName}>
            {disableBrowserHint ? (
                <span className={classNames('filter-pill__name ellipsis')}>{pillName}</span>
            ) : (
                <EllipsisTextHint mix={classes.pill__name}>{pillName}</EllipsisTextHint>
            )}
        </Button>
    );
};

FilterPill.displayName = 'FilterPill';
